import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { convertToXml, loadECMRFromLocalStorage, saveECMRAsXml } from '../services/eCMRService';
import { ECMR } from '../types/eCMR';

const ExportECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [xml, setXml] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        const loadedEcmr = loadECMRFromLocalStorage(id);
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }
        
        setEcmr(loadedEcmr);
        
        // Convertir a XML
        const generatedXml = await convertToXml(loadedEcmr);
        setXml(generatedXml);
        
        setLoading(false);
      } catch (err) {
        setError(`Error al cargar e-CMR: ${(err as Error).message}`);
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);
  
  const handleExport = async () => {
    if (!ecmr) return;
    
    try {
      const filename = ecmr.ExchangedDocument?.ID 
        ? `ecmr_${ecmr.ExchangedDocument.ID}.xml` 
        : 'ecmr.xml';
        
      await saveECMRAsXml(ecmr, filename);
    } catch (err) {
      setError(`Error al exportar XML: ${(err as Error).message}`);
    }
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (error || !ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error || 'Error al cargar el e-CMR'}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Exportar e-CMR: {ecmr.ExchangedDocument?.ID || 'Sin título'}
        </h1>
        
        <div className="flex space-x-2">
          <button
            onClick={handleExport}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Descargar XML
          </button>
          
          <button
            onClick={() => navigate(`/view/${id}`)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Volver
          </button>
        </div>
      </div>
      
      <div className="bg-white shadow-md rounded p-4 overflow-auto text-sm">
        <h2 className="text-xl font-semibold mb-4">Vista previa XML</h2>
        <pre className="whitespace-pre-wrap">{xml}</pre>
      </div>
    </div>
  );
};

export default ExportECMR;